import { fromJS, List } from 'immutable';

import ProfileSection from 'models/ProfileSectionModel';
import { ProfileSectionOrder } from 'enums/profile-section-type';

import { createReducer } from '../utils/helpers';
import { normalizeIds, normalizeModelList } from '../utils/Normalizer';

import {
  removeDocument,
  uploadDocuments,
  updateProfileSection,
  fundingSearchProfile,
  needUpdateProfileSection,
  markProfileSectionAsComplete,
  serviceabilityScore,
  serviceabilityPriceEstimates,
  SET_SNAPSHOT_CREATING,
} from './types';

export * from './actions';

const initialState = fromJS({
  byId: {},
  ids: [],
  fundingSearchId: false,
  network: { loading: false, error: false, integration: false },
});

const requestFundingSearchProfileSuccessReducer = (state, action) => {
  const ids = normalizeIds(action.response.sections, 'section');
  const byId = normalizeModelList(action.response.sections, ProfileSection.deserialize, 'section');

  return state.withMutations((s) =>
    s
      .setIn(['network', 'loading'], false)
      .setIn(['network', 'error'], false)
      .setIn(['byId'], byId)
      .setIn(['ids'], List(ProfileSectionOrder.filter((section) => ids.indexOf(section) >= 0))),
  );
};

const requestUploadDocumentsSuccessReducer = (state, action) => {
  if (!state.has('byId', 'documents')) {
    return state;
  }

  const sectionIndex = state
    .getIn(['byId', 'documents', 'data'])
    .findIndex((docType) => docType.id === action.documentType);

  return state.mergeIn(['byId', 'documents', 'data', sectionIndex, 'docs'], action.documents);
};

const requestRemoveDocumentSuccessReducer = (state, action) => {
  if (!state.has('byId', 'documents')) {
    return state;
  }

  const sectionIndex = state
    .getIn(['byId', 'documents', 'data'])
    .findIndex((docType) => docType.id === action.documentType);

  return state.updateIn(['byId', 'documents', 'data', sectionIndex, 'docs'], (documents) =>
    documents.filter((doc) => doc.id !== action.documentId),
  );
};

const requestProfileSectionUpdate = (state, action) =>
  state.withMutations((s) =>
    s
      .setIn(['byId', action.sectionId, 'network', 'loading'], true)
      .setIn(['byId', action.sectionId, 'network', 'error'], false),
  );

export default createReducer(initialState, {
  [fundingSearchProfile.request]: (state, action) =>
    state.withMutations((s) =>
      s.setIn(['network', 'loading'], true).setIn(['fundingSearchId'], action.fundingSearchId),
    ),
  [fundingSearchProfile.success]: requestFundingSearchProfileSuccessReducer,
  [fundingSearchProfile.failure]: (state, action) =>
    state.withMutations((s) =>
      s.setIn(['network', 'loading'], false).setIn(['network', 'error'], action.error),
    ),
  [updateProfileSection.request]: requestProfileSectionUpdate,
  [markProfileSectionAsComplete.request]: requestProfileSectionUpdate,
  [updateProfileSection.success]: (state, action) =>
    state.withMutations((s) =>
      s
        .setIn(['byId', action.sectionId, 'data'], action.response.data)
        .setIn(['byId', action.sectionId, 'completed'], action.response.completed)
        .setIn(['byId', action.sectionId, 'updateRequested'], action.response.updateRequested)
        .setIn(['byId', action.sectionId, 'network', 'loading'], false)
        .setIn(['byId', action.sectionId, 'network', 'error'], false),
    ),
  [updateProfileSection.failure]: (state, action) =>
    state.withMutations((s) =>
      s
        .setIn(['byId', action.sectionId, 'network', 'loading'], false)
        .setIn(['byId', action.sectionId, 'network', 'error'], action.error),
    ),

  [uploadDocuments.success]: requestUploadDocumentsSuccessReducer,
  [removeDocument.success]: requestRemoveDocumentSuccessReducer,

  [needUpdateProfileSection.success]: (state, action) =>
    state.setIn(['byId', action.sectionId, 'updateRequested'], true),

  [serviceabilityScore.request]: (state, action) =>
    state.withMutations((s) => s.setIn(['byId', action.sectionId, 'network', 'loading'], true)),
  [serviceabilityScore.success]: (state, action) =>
    state.withMutations((s) =>
      s
        .setIn(
          ['byId', action.sectionId, 'score', 'debtServiceCoverage'],
          action.response.debtServiceCoverage,
        )
        .setIn(
          ['byId', action.sectionId, 'score', 'monthlyRepayment'],
          action.response.monthlyRepayment,
        )
        .setIn(
          ['byId', action.sectionId, 'score', 'repaymentInterest'],
          action.response.repaymentInterest,
        )
        .setIn(
          ['byId', action.sectionId, 'score', 'totalRepayment'],
          action.response.totalRepayment,
        )
        .setIn(['byId', action.sectionId, 'network', 'loading'], false)
        .setIn(['byId', action.sectionId, 'network', 'error'], false),
    ),
  [serviceabilityScore.failure]: (state, action) =>
    state.withMutations((s) =>
      s
        .setIn(['byId', action.sectionId, 'network', 'loading'], false)
        .setIn(['byId', action.sectionId, 'network', 'error'], action.error),
    ),

  [serviceabilityPriceEstimates.request]: (state, action) =>
    state.withMutations((s) => s.setIn(['fundingSearchId'], action.fundingSearchId)),
  [serviceabilityPriceEstimates.success]: (state, action) =>
    state.withMutations((s) =>
      s
        .setIn(['byId', action.sectionId, 'data', 'rate'], action.response.median)
        .setIn(['byId', action.sectionId, 'network', 'error'], false),
    ),
  [serviceabilityPriceEstimates.failure]: (state, action) =>
    state.withMutations((s) => s.setIn(['network', 'error'], action.error)),
  [SET_SNAPSHOT_CREATING]: (state, action) =>
    state.setIn(['network', 'integration'], action.isLoading),
});
